import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NbToggleModule, NbIconModule } from '@nebular/theme';
import * as echarts from 'echarts';

interface SalesData {
  date: string;
  sales: number;
}

interface MonthData {
  name: string;
  sales: number;
}

export type DisplayMode = 'period' | 'cumulative';
export type ViewOption = 'toggle' | 'graph' | 'total';

@Component({
  selector: 'pl-sales-by-period-graphs',
  standalone: true,
  imports: [CommonModule, FormsModule, NbToggleModule, NbIconModule],
  templateUrl: './sales-by-period-graphs.component.html',
  styleUrls: ['./sales-by-period-graphs.component.scss']
})
export class SalesByPeriodGraphsComponent implements OnInit, AfterViewInit, OnDestroy{
  @Input() viewOption: ViewOption = 'toggle';
  @Input() defaultMode: DisplayMode = 'period';

  private weekChart: echarts.ECharts | null = null;
  private monthChart: echarts.ECharts | null = null;
  private quarterChart: echarts.ECharts | null = null;
  private ytdChart: echarts.ECharts | null = null;

  // Add cached data properties
  private cachedWeekData: SalesData[] | null = null;
  private cachedMonthData: SalesData[] | null = null;
  private cachedQuarterData: SalesData[] | null = null;
  private cachedYTDData: SalesData[] | null = null;

  showCumulative: boolean = false;
  cardStates = {
    week: false,
    month: false,
    quarter: false,
    ytd: false
  };

  ngOnInit() {
    if (this.viewOption === 'total') {
      this.showCumulative = true;
    } else if (this.viewOption === 'graph') {
      this.showCumulative = false;
    } else {
      this.showCumulative = this.defaultMode === 'cumulative';
    }
    // Initialize cached data on component init
    this.initializeCachedData();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.initializeCharts();
    }, 100);
  }

  ngOnDestroy() {
    if (this.weekChart) this.weekChart.dispose();
    if (this.monthChart) this.monthChart.dispose();
    if (this.quarterChart) this.quarterChart.dispose();
    if (this.ytdChart) this.ytdChart.dispose();
  }

  // Initialize all cached data once
  private initializeCachedData() {
    this.cachedWeekData = this.generateWeekDataInternal();
    this.cachedMonthData = this.generateMonthDataInternal();
    this.cachedQuarterData = this.generateQuarterDataInternal();
    this.cachedYTDData = this.generateYTDDataInternal();
  }

  // Internal methods that actually generate the data with randomization
  private generateWeekDataInternal(): SalesData[] {
    const data: SalesData[] = [];
    const today = new Date();
    const currentDay = today.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);

    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      let baseSales = 15000;
      const dayOfWeek = date.getDay();
      
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        baseSales *= 0.7;
      }
      if (dayOfWeek >= 2 && dayOfWeek <= 4) {
        baseSales *= 1.2;
      }
      
      const sales = Math.round(baseSales + (Math.random() - 0.5) * 5000);
      
      data.push({
        date: `${dayName} ${monthDay}`,
        sales: sales
      });
    }

    return data;
  }

  private generateMonthDataInternal(): SalesData[] {
    const data: SalesData[] = [];
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      const dayOfMonth = date.getDate();
      let baseSales = 18000;
      
      if (dayOfMonth > 25) {
        baseSales *= 1.3;
      }
      if (dayOfMonth >= 10 && dayOfMonth <= 15) {
        baseSales *= 0.8;
      }
      const dayOfWeek = date.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        baseSales *= 0.7;
      }
      
      const sales = Math.round(baseSales + (Math.random() - 0.5) * 6000);
      
      data.push({
        date: dayStr,
        sales: sales
      });
    }

    return data;
  }

  private generateQuarterDataInternal(): SalesData[] {
    const data: SalesData[] = [];
    const today = new Date();
    const currentQuarter = Math.floor(today.getMonth() / 3);
    const quarterStartMonth = currentQuarter * 3;
    
    for (let monthOffset = 0; monthOffset < 3; monthOffset++) {
      const month = quarterStartMonth + monthOffset;
      if (month > today.getMonth()) break;
      
      const monthDate = new Date(today.getFullYear(), month, 1);
      const monthName = monthDate.toLocaleDateString('en-US', { month: 'short' });
      
      let baseSales = 450000;
      if (monthOffset === 0) baseSales *= 0.8;
      if (monthOffset === 2) baseSales *= 1.3;
      
      const sales = Math.round(baseSales + (Math.random() - 0.5) * 80000);
      
      data.push({
        date: monthName,
        sales: sales
      });
    }

    return data;
  }

  private generateYTDDataInternal(): SalesData[] {
    const data: SalesData[] = [];
    const today = new Date();
    const currentMonth = today.getMonth();
    
    for (let month = 0; month <= currentMonth; month++) {
      const date = new Date(today.getFullYear(), month, 1);
      const monthStr = date.toLocaleDateString('en-US', { month: 'short' });
      
      let baseSales = 520000;
      
      if (month === 11 || month === 0) baseSales *= 1.4;
      if (month >= 5 && month <= 7) baseSales *= 1.2;
      if (month >= 1 && month <= 2) baseSales *= 0.8;
      
      const sales = Math.round(baseSales + (Math.random() - 0.5) * 100000);
      
      data.push({
        date: monthStr,
        sales: sales
      });
    }

    return data;
  }

  onToggleChange() {
    this.initializeCharts();
  }

  onCardHover(card: string, isHovered: boolean) {
    this.cardStates[card as keyof typeof this.cardStates] = isHovered;
  }

  getModeLabel(): string {
    if (this.viewOption === 'graph') {
      return this.showCumulative ? 'Graph View - Cumulative' : 'Graph View - Period';
    } else if (this.viewOption === 'total') {
      return 'Totals View';
    }
    return '';
  }

  // Date utility methods
  getCurrentWeekRange(): string {
    const today = new Date();
    const currentDay = today.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    const startStr = monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    
    return `${startStr} - ${endStr}`;
  }

  getCurrentMonthName(): string {
    const today = new Date();
    return today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  getCurrentQuarterName(): string {
    const today = new Date();
    const quarter = Math.floor(today.getMonth() / 3) + 1;
    return `Q${quarter} ${today.getFullYear()}`;
  }

  getCurrentYear(): string {
    return new Date().getFullYear().toString();
  }

  getCurrentMonth(): number {
    return new Date().getMonth();
  }

  getCurrentQuarter(): number {
    const today = new Date();
    return Math.floor(today.getMonth() / 3) + 1;
  }

  // Chart initialization methods
  private initializeCharts() {
    this.initWeekChart();
    this.initMonthChart();
    this.initQuarterChart();
    this.initYTDChart();
  }

  private initWeekChart() {
    const chartDom = document.getElementById('weekChart');
    if (!chartDom) return;

    if (this.weekChart) {
      this.weekChart.dispose();
    }

    this.weekChart = echarts.init(chartDom);
    const weekData = this.generateWeekData();
    const displayData = this.showCumulative ? this.calculateCumulative(weekData) : weekData;

    const option = {
      title: {
        show: false  // This hides the title
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const data = params[0];
          const prefix = this.showCumulative ? 'Cumulative' : 'Sales';
          return `${data.name}<br/>${prefix}: R${data.value.toLocaleString()}`;
        }
      },
      grid: {
        left: '8%',
        right: '8%',
        bottom: '15%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: displayData.map(d => d.date.split(' ')[0]),
        axisLabel: {
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: 'R{value}',
          fontSize: 10
        }
      },
      series: [{
        name: 'Sales',
        type: 'line',
        smooth: true,
        data: displayData.map(d => d.sales),
        lineStyle: {
          color: '#667eea',
          width: 3
        },
        itemStyle: {
          color: '#667eea'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(102, 126, 234, 0.3)' },
            { offset: 1, color: 'rgba(102, 126, 234, 0.1)' }
          ])
        }
      }]
    };

    this.weekChart.setOption(option);
  }

  private initMonthChart() {
    const chartDom = document.getElementById('monthChart');
    if (!chartDom) return;

    if (this.monthChart) {
      this.monthChart.dispose();
    }

    this.monthChart = echarts.init(chartDom);
    const monthData = this.generateMonthData();
    const displayData = this.showCumulative ? this.calculateCumulative(monthData) : monthData;

    const option = {
      title: {
        show: false  // This hides the title
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const data = params[0];
          const prefix = this.showCumulative ? 'Cumulative' : 'Sales';
          return `${data.name}<br/>${prefix}: R${data.value.toLocaleString()}`;
        }
      },
      grid: {
        left: '8%',
        right: '8%',
        bottom: '15%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: displayData.map((d, i) => i % 3 === 0 ? d.date : ''),
        axisLabel: {
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: 'R{value}',
          fontSize: 10
        }
      },
      series: [{
        name: 'Sales',
        type: 'line',
        smooth: true,
        data: displayData.map(d => d.sales),
        lineStyle: {
          color: '#70AD47',
          width: 3
        },
        itemStyle: {
          color: '#70AD47'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(112, 173, 71, 0.3)' },
            { offset: 1, color: 'rgba(112, 173, 71, 0.1)' }
          ])
        }
      }]
    };

    this.monthChart.setOption(option);
  }

  private initQuarterChart() {
    const chartDom = document.getElementById('quarterChart');
    if (!chartDom) return;

    if (this.quarterChart) {
      this.quarterChart.dispose();
    }

    this.quarterChart = echarts.init(chartDom);
    const quarterData = this.generateQuarterData();
    const displayData = this.showCumulative ? this.calculateCumulative(quarterData) : quarterData;

    const option = {
      title: {
        show: false  // This hides the title
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const data = params[0];
          const prefix = this.showCumulative ? 'Cumulative' : 'Sales';
          return `${data.name}<br/>${prefix}: R${data.value.toLocaleString()}`;
        }
      },
      grid: {
        left: '8%',
        right: '8%',
        bottom: '15%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: displayData.map(d => d.date),
        axisLabel: {
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: 'R{value}',
          fontSize: 10
        }
      },
      series: [{
        name: 'Sales',
        type: 'line',
        smooth: true,
        data: displayData.map(d => d.sales),
        lineStyle: {
          color: '#e74c3c',
          width: 3
        },
        itemStyle: {
          color: '#e74c3c'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(231, 76, 60, 0.3)' },
            { offset: 1, color: 'rgba(231, 76, 60, 0.1)' }
          ])
        }
      }]
    };

    this.quarterChart.setOption(option);
  }

  private initYTDChart() {
    const chartDom = document.getElementById('ytdChart');
    if (!chartDom) return;

    if (this.ytdChart) {
      this.ytdChart.dispose();
    }

    this.ytdChart = echarts.init(chartDom);
    const ytdData = this.generateYTDData();
    const displayData = this.showCumulative ? this.calculateCumulative(ytdData) : ytdData;

    const option = {
      title: {
        show: false  // This hides the title
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const data = params[0];
          const prefix = this.showCumulative ? 'Cumulative' : 'Sales';
          return `${data.name}<br/>${prefix}: R${data.value.toLocaleString()}`;
        }
      },
      grid: {
        left: '8%',
        right: '8%',
        bottom: '15%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: displayData.map((d, i) => i % 5 === 0 ? d.date : ''),
        axisLabel: {
          fontSize: 10,
          rotate: 45
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: 'R{value}',
          fontSize: 10
        }
      },
      series: [{
        name: 'Sales',
        type: 'line',
        smooth: true,
        data: displayData.map(d => d.sales),
        lineStyle: {
          color: '#FFC000',
          width: 3
        },
        itemStyle: {
          color: '#FFC000'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(255, 192, 0, 0.3)' },
            { offset: 1, color: 'rgba(255, 192, 0, 0.1)' }
          ])
        }
      }]
    };

    this.ytdChart.setOption(option);
  }

  // Data generation methods
  generateWeekData(): SalesData[] {
    return this.cachedWeekData || [];
    const data: SalesData[] = [];
    const today = new Date();
    const currentDay = today.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);

    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      let baseSales = 15000;
      const dayOfWeek = date.getDay();
      
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        baseSales *= 0.7;
      }
      if (dayOfWeek >= 2 && dayOfWeek <= 4) {
        baseSales *= 1.2;
      }
      
      const sales = Math.round(baseSales + (Math.random() - 0.5) * 5000);
      
      data.push({
        date: `${dayName} ${monthDay}`,
        sales: sales
      });
    }

    return data;
  }

  generateMonthData(): SalesData[] {
    return this.cachedMonthData || [];
    const data: SalesData[] = [];
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      const dayOfMonth = date.getDate();
      let baseSales = 18000;
      
      // Month-end boost
      if (dayOfMonth > 25) {
        baseSales *= 1.3;
      }
      // Mid-month dip
      if (dayOfMonth >= 10 && dayOfMonth <= 15) {
        baseSales *= 0.8;
      }
      // Weekend effect
      const dayOfWeek = date.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        baseSales *= 0.7;
      }
      
      const sales = Math.round(baseSales + (Math.random() - 0.5) * 6000);
      
      data.push({
        date: dayStr,
        sales: sales
      });
    }

    return data;
  }

  private generateQuarterData(): SalesData[] {
    return this.cachedQuarterData || [];
    const data: SalesData[] = [];
    const today = new Date();
    const currentQuarter = Math.floor(today.getMonth() / 3);
    const quarterStartMonth = currentQuarter * 3;
    
    // Generate monthly data points for the current quarter
    for (let monthOffset = 0; monthOffset < 3; monthOffset++) {
      const month = quarterStartMonth + monthOffset;
      if (month > today.getMonth()) break; // Don't generate future months
      
      const monthDate = new Date(today.getFullYear(), month, 1);
      const monthName = monthDate.toLocaleDateString('en-US', { month: 'short' });
      
      // Quarter pattern: slow start, strong finish
      let baseSales = 450000;
      if (monthOffset === 0) baseSales *= 0.8; // First month slower
      if (monthOffset === 2) baseSales *= 1.3; // Last month stronger
      
      const sales = Math.round(baseSales + (Math.random() - 0.5) * 80000);
      
      data.push({
        date: monthName,
        sales: sales
      });
    }

    return data;
  }

  generateYTDData(): SalesData[] {
    return this.cachedYTDData || [];
    const data: SalesData[] = [];
    const today = new Date();
    const currentMonth = today.getMonth();
    
    for (let month = 0; month <= currentMonth; month++) {
      const date = new Date(today.getFullYear(), month, 1);
      const monthStr = date.toLocaleDateString('en-US', { month: 'short' });
      
      let baseSales = 520000;
      
      // Seasonal patterns
      if (month === 11 || month === 0) baseSales *= 1.4; // Holiday season
      if (month >= 5 && month <= 7) baseSales *= 1.2; // Summer boost
      if (month >= 1 && month <= 2) baseSales *= 0.8; // Post-holiday dip
      
      const sales = Math.round(baseSales + (Math.random() - 0.5) * 100000);
      
      data.push({
        date: monthStr,
        sales: sales
      });
    }

    return data;
  }

  // Calculation methods
  private calculateCumulative(data: SalesData[]): SalesData[] {
    let cumulative = 0;
    return data.map(item => {
      cumulative += item.sales;
      return {
        ...item,
        sales: cumulative
      };
    });
  }

  // Total calculation methods
  getWeekTotal(): number {
    return this.generateWeekData().reduce((sum, item) => sum + item.sales, 0);
  }

  getMonthTotal(): number {
    return this.generateMonthData().reduce((sum, item) => sum + item.sales, 0);
  }

  getQuarterTotal(): number {
    return this.generateQuarterData().reduce((sum, item) => sum + item.sales, 0);
  }

  getYTDTotal(): number {
    return this.generateYTDData().reduce((sum, item) => sum + item.sales, 0);
  }

  // Average and best day calculations
  getMonthAverage(): number {
    const monthData = this.generateMonthData();
    return Math.round(this.getMonthTotal() / monthData.length);
  }

  getBestDay(period: string): number {
    let data: SalesData[] = [];
    
    switch (period) {
      case 'week':
        data = this.generateWeekData();
        break;
      case 'month':
        data = this.generateMonthData();
        break;
      case 'quarter':
        data = this.generateQuarterData();
        break;
      case 'ytd':
        data = this.generateYTDData();
        break;
    }
    
    return Math.max(...data.map(item => item.sales));
  }

  // Quarter-specific methods
  getQuarterMonths(): MonthData[] {
    const today = new Date();
    const currentQuarter = Math.floor(today.getMonth() / 3);
    const quarterStartMonth = currentQuarter * 3;
    const months: MonthData[] = [];
    
    for (let monthOffset = 0; monthOffset < 3; monthOffset++) {
      const month = quarterStartMonth + monthOffset;
      if (month > today.getMonth()) break;
      
      const monthDate = new Date(today.getFullYear(), month, 1);
      const monthName = monthDate.toLocaleDateString('en-US', { month: 'long' });
      
      // Generate sales for each month in quarter
      let baseSales = 450000;
      if (monthOffset === 0) baseSales *= 0.8;
      if (monthOffset === 2) baseSales *= 1.3;
      
      const sales = Math.round(baseSales + (Math.random() - 0.5) * 80000);
      
      months.push({
        name: monthName,
        sales: sales
      });
    }
    
    return months;
  }

  // Projection methods
  getProjectedAnnual(): number {
    const ytdTotal = this.getYTDTotal();
    const currentMonth = new Date().getMonth() + 1; // 1-based
    const monthsRemaining = 12 - currentMonth;
    
    if (monthsRemaining === 0) return ytdTotal;
    
    const monthlyAverage = ytdTotal / currentMonth;
    return Math.round(ytdTotal + (monthlyAverage * monthsRemaining));
  }
}

